import type {
	CourseAiCourse,
	CourseAiSkill,
	CourseAiTopic,
} from 'src/apis/courseAiAPI'

export type EditableSkill = Extract<CourseAiSkill, 'writing' | 'dictation'>

const SKILL_KEYWORDS: Record<EditableSkill, string[]> = {
	writing: ['writing', 'write'],
	dictation: ['dictation', 'dictate'],
}

export const normalizeText = (value?: string | null) =>
	String(value || '')
		.trim()
		.toLowerCase()

export const matchCourseBySkill = (
	courses: CourseAiCourse[],
	skill: EditableSkill,
): CourseAiCourse | null => {
	const keywords = SKILL_KEYWORDS[skill]
	let best: CourseAiCourse | null = null
	let bestScore = -Infinity

	courses.forEach((course) => {
		const type = normalizeText(course.type)
		const skillField = normalizeText(course.skill)
		const slug = normalizeText(course.slug)
		const name = normalizeText(course.name)
		const title = normalizeText(course.title)
		const haystack = [type, skillField, slug, name, title].join(' ')
		if (!keywords.some((keyword) => haystack.includes(keyword))) return

		let score = 10
		if (type === skill) score += 100
		if (skillField === skill) score += 80
		if (name === `course: ${skill}` || title === `course: ${skill}`) score += 60
		else if (
			name.startsWith(`course: ${skill}`) ||
			name.startsWith(`course ${skill}`) ||
			title.startsWith(`course: ${skill}`) ||
			title.startsWith(`course ${skill}`)
		) {
			score += 40
		}
		if (name.includes('test') || name.includes('e2e') || title.includes('test')) {
			score -= 40
		}
		if (name.includes('generated') || title.includes('generated')) {
			score -= 15
		}

		if (score > bestScore) {
			bestScore = score
			best = course
		}
	})

	return best
}

export const isDeletedLesson = (topic: CourseAiTopic) => {
	const deletedAt = topic.deleted_at ?? topic.deletedAt
	if (deletedAt) return true
	if (topic.is_deleted === true) return true
	const status = normalizeText(String(topic.status || ''))
	return status === 'deleted'
}

/** The lessons of one course that the Lesson list shows: not deleted, and of that course. */
export const liveLessonsOfCourse = (
	rows: CourseAiTopic[],
	courseId: string,
): CourseAiTopic[] =>
	rows.filter((topic) => {
		if (isDeletedLesson(topic)) return false
		return topic.course_id ? topic.course_id === courseId : true
	})

/** The day count of every AI course: fixed Day 1 to 90, complete only with 90 lessons or more. */
export const COURSE_DAYS = 90

// The server returns 50 topics unless asked for more, and the length warning and the count must see every topic.
export const DICTATION_LIST_LIMIT = 500

export type LessonCounts = Partial<Record<EditableSkill, number>>

export const isCourseComplete = (count: number) => count >= COURSE_DAYS
